function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { lighten, rgba } from 'polished';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { withSpacing } from '../lib/hoc/withSpacing';
import animations from '../styles/animations';
import { padding, shade, themify, tint } from '../styles/mixins';
import { Icon } from './Icon';
export const Label = styled.label.withConfig({
  displayName: "Input__Label",
  componentId: "sc-1soazrv-0"
})(["display:block;font-size:", ";color:", ";text-align:left;"], x => x.theme.type.scale[x.$labelSize || x.$fontSize + 1], x => x.$labelColor);
export const StyledInput = styled.input.withConfig({
  displayName: "Input__StyledInput",
  componentId: "sc-1soazrv-1"
})(["appearance:none;display:inline-block;vertical-align:", ";margin:0;padding:1em;height:3em;width:100%;background:", ";color:", ";font-size:", ";text-align:", ";border:1px solid ", ";border-radius:", ";line-height:1;transition:border-color 0.25s;&:hover{border-color:", ";}&:focus{outline:none;border-color:", ";}&:disabled{background:", ";color:", ";}&::-webkit-input-placeholder{color:", ";}&::-moz-placeholder{color:", ";}&:-moz-placeholder{color:", ";}&:-ms-input-placeholder{color:", ";}"], x => x.$hasAddon ? 'middle' : 'top', x => x.$bgColor, x => x.$color, x => x.theme.type.scale[x.$fontSize], x => x.$textAlign, x => x.$borderColor, x => x.$hasAddon ? x.$addonSide === 'start' ? '0 3px 3px 0' : '3px 0 0 3px' : '3px', x => shade(0.9, x.$borderColor), x => tint(0.9, x.theme.colors.green), x => x.theme.colors.grayLight, x => x.theme.colors.grayDark, x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5), x => rgba(tint(0.75, x.$color), 0.5));
export const InputWrapper = styled.div.withConfig({
  displayName: "Input__InputWrapper",
  componentId: "sc-1soazrv-2"
})(["position:relative;display:flex;flex-flow:row nowrap;width:100%;"]);
export const InputAddon = styled.label.withConfig({
  displayName: "Input__InputAddon",
  componentId: "sc-1soazrv-3"
})(["", ";display:flex;flex-flow:row nowrap;flex-basis:3em;align-items:center;justify-content:center;height:3em;white-space:nowrap;font-size:", ";font-weight:500;border-style:solid;border-color:", ";border-width:", ";border-radius:", ";background:", ";cursor:pointer;transition:all 0.25s;&:hover{background:", ";border-color:", ";}&:active{background:", ";box-shadow:inset 0 0 3px 3px ", ";text-shadow:1px 1px 3px ", ";}"], x => padding(x.$addonPadding), x => x.theme.type.scale[x.$fontSize], x => x.$borderColor, x => x.$addonSide === 'start' ? '1px 0 1px 1px' : '1px 1px 1px 0', x => x.$addonSide === 'start' ? '3px 0 0 3px' : '0 3px 3px 0', x => x.$addonColor, x => shade(0.9, x.$addonColor), x => shade(0.8, x.$borderColor), x => shade(0.85, x.$addonColor), x => shade(0.8, x.$addonColor), x => shade(0.8, x.$addonColor));
export const InputIcon = styled(Icon).withConfig({
  displayName: "Input__InputIcon",
  componentId: "sc-1soazrv-4"
})(["display:block;animation:", " 1s infinite;animation-timing-function:linear;"], x => x.iconAnimation ? animations[x.iconAnimation] : '');
export const Error = styled.span.withConfig({
  displayName: "Input__Error",
  componentId: "sc-1soazrv-5"
})(["float:right;color:", ";font-size:", ";font-weight:600;white-space:nowrap;"], x => x.theme.colors.red, x => x.theme.type.scale[6]);

function wrapInput(children) {
  return /*#__PURE__*/React.createElement(InputWrapper, null, children);
}

export const InputFunc = (_ref) => {
  let {
    addonColor = 'grayLighter',
    addonContent,
    addonPadding = 1,
    addonSide,
    bgColor = 'white',
    borderColor = 'gray',
    className,
    color = 'grayBlack',
    error,
    fontSize = 5,
    form,
    icon,
    iconAnimation,
    iconColor = 'grayDarker',
    id,
    innerRef,
    label,
    labelColor = 'grayDark',
    labelSize = 6,
    name,
    placeholder,
    textAlign = 'left',
    touched,
    unstyled = false
  } = _ref,
      inputProps = _objectWithoutProperties(_ref, ["addonColor", "addonContent", "addonPadding", "addonSide", "bgColor", "borderColor", "className", "color", "error", "fontSize", "form", "icon", "iconAnimation", "iconColor", "id", "innerRef", "label", "labelColor", "labelSize", "name", "placeholder", "textAlign", "touched", "unstyled"]);

  /** A unique ID randomly generated on mount. So in the case no `id` or `name` is passed along, the label always works as it should */
  const fallbackId = useMemo(() => Math.random().toString(36).substr(2, 5), []); // TODO: NEXT: This screws up SSR

  const AddonComponent = addonContent || icon ? /*#__PURE__*/React.createElement(InputAddon, {
    $addonColor: touched ? lighten(0.35, themify(error ? 'red' : 'green')) : themify(addonColor),
    $addonPadding: addonPadding,
    $addonSide: addonSide,
    $borderColor: themify(touched ? error ? 'red' : 'green' : borderColor),
    $fontSize: fontSize,
    htmlFor: name || fallbackId,
    key: "inputAddon"
  }, icon ? /*#__PURE__*/React.createElement(InputIcon, {
    fill: themify(touched ? error ? 'red' : 'green' : iconColor || addonColor),
    icon: icon,
    iconAnimation: iconAnimation,
    solid: true,
    stroke: "none"
  }) : addonContent || '') : /*#__PURE__*/React.createElement(React.Fragment, {
    key: "inputAddonEmpty"
  });
  const InputComponent = unstyled ? /*#__PURE__*/React.createElement("input", {
    key: "inputElement",
    id: id || name || fallbackId,
    name: name,
    placeholder: placeholder || (placeholder !== '' && typeof label === 'string' ? label : ''),
    ref: innerRef
  }) : /*#__PURE__*/React.createElement(StyledInput, _extends({
    $addonSide: addonSide,
    $bgColor: themify(bgColor),
    $borderColor: themify(touched ? error ? 'red' : 'green' : borderColor),
    $color: themify(color),
    $fontSize: fontSize,
    $hasAddon: !!(addonSide && (icon || addonContent)),
    $textAlign: textAlign,
    key: "inputElement",
    id: id || name || fallbackId,
    name: name,
    placeholder: placeholder || (typeof label === 'string' ? label : undefined),
    ref: innerRef
  }, inputProps));

  const renderInput = side => {
    if (unstyled) return InputComponent;
    if (side === 'start') return wrapInput([AddonComponent, InputComponent]);
    if (side === 'end') return wrapInput([InputComponent, AddonComponent]);
    return InputComponent;
  };

  return /*#__PURE__*/React.createElement("div", {
    className: className
  }, label && /*#__PURE__*/React.createElement(Label, {
    htmlFor: name,
    $fontSize: fontSize,
    $labelColor: themify(labelColor),
    $labelSize: labelSize
  }, label, touched && error && /*#__PURE__*/React.createElement(Error, null, error)), renderInput(addonSide));
}; // TODO: Hacky hack for assigning sub-component with proper type

const BaseInput = withSpacing(InputFunc, {
  mb: 1
});
export const Input = BaseInput;
Input.Label = Label;